import { Form, Formik } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { GiConfirmed } from 'react-icons/gi';
import { toast } from 'react-toastify';
import { sectionAnimVariants } from '../../../constants/animationVariants';
import useAppointmentActions from '../../../hooks/APIHooks/appointments/useAppointmentActions';
import { appointmentSlotSchema } from '../../../schemas';
import { AppointmentSlot } from '../../../types/appointments';
import { Associate } from '../../../types/associates';
import { convertMinutesToHours } from '../../../utils/timeAndDate';
import BookingDetailsTable from '../../AppointmentsComps/BookingComps/BookingDetailsTable';
import InputFieldBadge from '../Badges/InputFieldBadge';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaCheckboxField from '../Inputs/AcademiaCheckboxField';
import AcademiaDatePicker from '../Inputs/AcademiaDatePicker';
import AcademiaInputField from '../Inputs/AcademiaInputField';
import AcademiaModalLoading from '../Loadings/AcademiaModalLoading';
import ModalTimeSlot from './ModalTimeSlot';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';
import ReferralSlotBookingConsentModal from './ReferralSlotBookingConsentModal';

const ReferralAvailableSlotModal = ({
  serviceId,
  associate,
  handleModalClose,
  handleAdditionalModalClose,
}: {
  serviceId: number;
  associate: Associate;
  handleModalClose: () => void;
  handleAdditionalModalClose?: () => void;
}) => {
  // integration of react hooks here
  const [showConsentModal, setShowConsentModal] = useState<boolean>(false);
  const [isAvailableSlotLoading, setIsAvailableSlotLoading] =
    useState<boolean>(false);

  const [availableSlots, setAvailableSlots] = useState<Array<AppointmentSlot>>(
    []
  );
  const [maxMinIndex, setMaxMinIndex] = useState<Array<number>>([]);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<
    Array<{ index: number; slot: string }>
  >([]);

  const [selectedSlotDetails, setSelectedSlotDetails] = useState<any>();

  const [appointmentDate, setAppointmentDate] = useState<string>(
    moment(new Date()).format('YYYY-MM-DD')
  );

  // integration of custom API hooks here
  const { getAvailableSlotsHandler } = useAppointmentActions('');

  // fetching available slots based on associate id and appointment date here
  useEffect(() => {
    if (associate?.id && appointmentDate) {
      setAvailableSlots([]);
      setIsAvailableSlotLoading(true);

      getAvailableSlotsHandler
        .mutateAsync({
          body: {
            associate_id: associate.id,
            service_id: serviceId,
            date: appointmentDate,
            current_time: new Date().toLocaleTimeString('en-GB', {
              hour12: false,
            }),
          },
        })
        .then((res) => {
          if (res.is_success) {
            setAvailableSlots(res.data);
          }
        })
        .catch((error) => {
          console.log(error);
          setAvailableSlots([]);
        })
        .finally(() => setIsAvailableSlotLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [associate.id, appointmentDate]);

  // sorting selected appointments based on index here
  useEffect(() => {
    if (selectedTimeSlots.length) {
      const sortedTimeSlots = selectedTimeSlots.sort(
        (slotA, slotB) => slotA.index - slotB.index
      );

      setSelectedTimeSlots(sortedTimeSlots);

      // setting max & min index for disabling here
      setMaxMinIndex([
        sortedTimeSlots[0].index,
        sortedTimeSlots[sortedTimeSlots.length - 1].index,
      ]);
    }
  }, [selectedTimeSlots]);

  // handler function to pass selected slot details to consent modal declared here
  const availableSlotFormHandler = (values: any) => {
    setShowConsentModal(true);
    console.log('VALUES: ', values);
    setSelectedSlotDetails({
      time_slot: `${selectedTimeSlots[0].slot.split(' - ')[0]} - ${
        selectedTimeSlots[selectedTimeSlots.length - 1].slot.split(' - ')[1]
      }`,
      want_reminder: values.want_reminder,
      reminder_time: values.reminder_time,
    });
  };

  // handler function to update array on select time slot declared here
  const onSelectTimeSlotHandler = (index: number, slotDuration: string) => {
    if (selectedTimeSlots.length) {
      if (selectedTimeSlots.filter((slot) => slot.index === index).length) {
        const prevTimeSlot = selectedTimeSlots.find(
          (slot) => slot.index === index - 1
        );
        const nextTimeSlot = selectedTimeSlots.find(
          (slot) => slot.index === index + 1
        );

        if (
          typeof prevTimeSlot?.index === 'number' &&
          typeof nextTimeSlot?.index === 'number'
        ) {
          toast.warn('Cannot Deselect This Slot!!');
        } else {
          setSelectedTimeSlots((state) =>
            state.filter((slot) => slot.index !== index)
          );
        }
      } else {
        setSelectedTimeSlots((state) => [
          ...state,
          { index, slot: slotDuration },
        ]);
      }
    } else {
      setSelectedTimeSlots((state) => [
        ...state,
        { index, slot: slotDuration },
      ]);
    }
  };

  // function to check if time slot selected or not declared here
  const isTimeSlotSelected = (index: number): boolean => {
    return !!selectedTimeSlots.filter((slot) => slot.index === index).length;
  };

  // rendering referral associate available slot modal component here
  return (
    <>
      <ParentModal onClick={handleModalClose} customWidthClass='md:w-[750px]'>
        <div>
          <Formik
            initialValues={{
              date: '',
            }}
            onSubmit={() => {}}
          >
            {(props) => (
              <Form className='space-y-5'>
                <AcademiaDatePicker
                  name='date'
                  label='Select Appointment Date'
                  placeholder='DD-MM-YYYY'
                  minDate={new Date()}
                  defaultDate={new Date()}
                  setDateOnChange={(date) =>
                    setAppointmentDate(
                      moment(date, 'DD-MM-YYYY').format('YYYY-MM-DD')
                    )
                  }
                  isRequired
                />
              </Form>
            )}
          </Formik>
        </div>

        <div className='overflow-hidden'>
          {!isAvailableSlotLoading ? (
            availableSlots.length ? (
              <motion.div
                variants={sectionAnimVariants}
                initial='initial'
                animate='animate'
                className='space-y-3'
              >
                <div className='flex flex-col md:flex-row items-center justify-center gap-1'>
                  <ModalTitle titleText='Available slots' />

                  <p className='text-sm text-legal-dark-blue mr-4'>
                    (For{' '}
                    <span className='font-medium'>
                      {moment(appointmentDate, 'YYYY-MM-DD').format(
                        'DD-MM-YYYY'
                      )}
                    </span>
                    )
                  </p>
                </div>

                <Formik
                  initialValues={{
                    time_slot: '',
                    want_reminder: false,
                    reminder_time: '',
                  }}
                  validationSchema={appointmentSlotSchema}
                  onSubmit={availableSlotFormHandler}
                >
                  {(props) => (
                    <Form className='space-y-5'>
                      <>
                        <div className='grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4'>
                          <div className='h-[430px] space-y-4 overflow-y-auto p-2 academia-scrollbar border-2 border-dashed border-base-cyan rounded-lg'>
                            {availableSlots.map((slot, index) => (
                              <ModalTimeSlot
                                key={index}
                                index={index}
                                id={`time-slot-${index}`}
                                name={`time-slot-${index}`}
                                slot={slot}
                                onSelect={onSelectTimeSlotHandler}
                                isSlotSelected={isTimeSlotSelected}
                                isDisabled={
                                  selectedTimeSlots.length
                                    ? !(
                                        index >= maxMinIndex[0] - 1 &&
                                        index <=
                                          maxMinIndex[maxMinIndex.length - 1] +
                                            1
                                      )
                                    : false
                                }
                              />
                            ))}
                          </div>

                          <div className='flex items-center'>
                            <BookingDetailsTable
                              selectedTimeSlotCount={selectedTimeSlots.length}
                              creditsPerAppointment={
                                associate.credits_per_appointments[0]
                              }
                              slotDuration={associate.slot_durations[0]}
                              appointmentBufferTime={
                                associate.appointment_buffer_time
                              }
                            />
                          </div>
                        </div>

                        <p className='font-semibold text-xs text-base-red !mt-2'>
                          **Each additional slot adds{' '}
                          {associate.appointment_buffer_time} minutes and costs{' '}
                          {(
                            (associate.credits_per_appointments[0] /
                              associate.slot_durations[0]) *
                            associate.appointment_buffer_time
                          ).toLocaleString('en-US', {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 1,
                          })}{' '}
                          extra credits.**
                        </p>
                      </>

                      <motion.span
                        initial={{ y: -20, opacity: 0, display: 'none' }}
                        animate={
                          props.touched.time_slot && props.errors.time_slot
                            ? { y: 0, opacity: 1, display: 'block' }
                            : {
                                y: -20,
                                opacity: 0,
                                transitionEnd: { display: 'none' },
                              }
                        }
                        className='block mt-1 text-base-red text-base'
                      >
                        {props.errors.time_slot}
                      </motion.span>

                      {availableSlots.length ? (
                        <>
                          <AcademiaCheckboxField
                            name='want_reminder'
                            label='Want custom reminder?'
                          />

                          <AnimatePresence
                            initial={false}
                            mode='wait'
                            onExitComplete={() => null}
                          >
                            {props.values.want_reminder ? (
                              <motion.div
                                initial={{ y: -50, opacity: 0, height: 0 }}
                                animate={{
                                  y: 0,
                                  opacity: 1,
                                  height: 'fit-content',
                                }}
                                exit={{ y: -50, opacity: 0, height: 0 }}
                              >
                                <AcademiaInputField
                                  type='number'
                                  name='reminder_time'
                                  label='Reminder Time (Minutes)'
                                  placeholder='Enter reminder time here... (Max 120)'
                                  badge={
                                    props.values.reminder_time ? (
                                      <InputFieldBadge
                                        badgeText=''
                                        badgeValue={convertMinutesToHours(
                                          +props.values.reminder_time
                                        )}
                                        isValid={!props.errors.reminder_time}
                                      />
                                    ) : undefined
                                  }
                                  isRequired
                                />
                              </motion.div>
                            ) : null}
                          </AnimatePresence>

                          <AcademiaButton
                            title={
                              !selectedTimeSlots.length
                                ? 'Please, select at least one time slot first!'
                                : undefined
                            }
                            type='submit'
                            variant='premium'
                            additionalClassNames='!py-2'
                            icon={<GiConfirmed />}
                            fullWidth
                            isDisabled={!selectedTimeSlots.length}
                          >
                            Confirm Slot
                          </AcademiaButton>
                        </>
                      ) : null}
                    </Form>
                  )}
                </Formik>
              </motion.div>
            ) : (
              <p className='text-base-red font-medium text-center text-lg'>
                No available slots on this day!! <br /> Please, alter dates to
                check available slots.
              </p>
            )
          ) : (
            <AcademiaModalLoading />
          )}
        </div>
      </ParentModal>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showConsentModal ? (
          <ReferralSlotBookingConsentModal
            associate={associate}
            appointmentDate={appointmentDate}
            slotDetails={selectedSlotDetails}
            slotCount={selectedTimeSlots.length}
            handleModalClose={() => setShowConsentModal(false)}
            handleAdditionalModalClose={() => {
              handleModalClose();
              handleAdditionalModalClose && handleAdditionalModalClose();
            }}
          />
        ) : null}
      </AnimatePresence>
    </>
  );
};

export default ReferralAvailableSlotModal;
